import { useNavigate, useLocation } from 'react-router-dom';
import { show as IntercomShow } from '@intercom/messenger-js-sdk';
import { useState, useRef, useCallback, useEffect } from 'react';
import { ROUTE_LABS_ORDER_STEP_1, ROUTE_LABS_ALL } from '../../../constants/routes';
import DashboardNavigationBar from '../../../components/dashboard/navigation-bar/dashboard-navigation-bar';
import DashboardLayout from '@/components/dashboard/dashboard-layout/dashboard-layout';
import DashboardGrid from '@/components/dashboard/dashboard-grid/dashboard-grid';
import Resources from '@/components/resources/resources';
import { useCriticalLabNotifications, CriticalLabNotificationBanner } from '../notifications';
import {
  useListLabOrdersV1LabsOrdersGet,
  useGetLabOrderV1LabsOrdersOrderIdGet,
} from '../../../api/generated-catch-backend/laboratory-orders/laboratory-orders';
import { TopLevelOrderStatus } from '../../../api/generated-catch-backend/model';
import useAnalyticsCalls from '@/hooks/use-analytics';
import { BiomarkerDashboard } from './components/biomarker-dashboard';
import {
  LabsHeaderNoOrder,
  LabsHeaderOrdered,
  LabsHeaderError,
  LabsHeaderBiomarkers,
  type OrderInfo,
} from './components/labs-headers';
import { ScheduleVisitModal } from './components/schedule-visit-modal';
import { LabsProgressTracker } from './components/labs-progress-tracker';
import { LabsDownloadReport } from './components/labs-download-report';
import { getLabDisplayName } from './utils/lab-provider-mapping';
import { formatPatientAddress } from './utils/format-address';
import { useDownloadLabOrder } from '../labs-order/hooks/useDownloadLabOrder';
import { useDownloadLabResults } from '../labs-order/hooks/useDownloadLabResults';

/**
 * Maps TopLevelOrderStatus to user-friendly analytics status name
 */
function mapLabStatusForAnalytics(status: TopLevelOrderStatus | undefined): string {
  if (!status) {
    return 'Order Labs';
  }

  switch (status) {
    case TopLevelOrderStatus.eligible:
      return 'Order Labs';
    case TopLevelOrderStatus.ready_for_testing:
      return 'Ready for Testing';
    case TopLevelOrderStatus.pending_review:
      return 'Pending Physician Review';
    case TopLevelOrderStatus.results_ready:
      return 'Results Available';
    default:
      return 'Order Labs';
  }
}

export function Page() {
  const navigate = useNavigate();
  const { callModalViewed, callLinkClicked, setAnalyticsParams } = useAnalyticsCalls();
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const { downloadOrder } = useDownloadLabOrder();
  const { downloadResults } = useDownloadLabResults();

  // Fetch critical lab notifications
  const { notifications, dismissNotification, isDismissing } = useCriticalLabNotifications();

  // Fetch user's lab orders
  const { data: ordersData } = useListLabOrdersV1LabsOrdersGet();
  const orders = ordersData?.data || [];
  const latestOrder = orders[0];

  // Fetch detailed information for the most recent order
  const { data: orderDetail } = useGetLabOrderV1LabsOrdersOrderIdGet(latestOrder?.orderId || '', {
    query: {
      enabled: !!latestOrder?.orderId,
    },
  });

  const topLevelStatus = ordersData?.meta.status;
  setAnalyticsParams({ order_status: mapLabStatusForAnalytics(topLevelStatus) });

  if (mapLabStatusForAnalytics(topLevelStatus) !== 'Order Labs' && latestOrder && orderDetail) {
    setAnalyticsParams({
      order_status: mapLabStatusForAnalytics(topLevelStatus),
      catch_panel: 'standard_cancer_panel',
      order_id: latestOrder.orderId,
      lab_name: orderDetail.labProvider,
    });
  }

  // Handle notification actions - Must be defined before early return
  const handleViewResults = useCallback(
    async (notificationId: string) => {
      await dismissNotification(notificationId);
      navigate(`${ROUTE_LABS_ALL}?filter=critical`);
    },
    [dismissNotification, navigate],
  );

  const handleDismissNotification = useCallback(
    async (notificationId: string) => {
      await dismissNotification(notificationId);
    },
    [dismissNotification],
  );

  // Construct order info from actual order data
  const orderInfo: OrderInfo = {
    orderCode: orderDetail?.orderId || latestOrder?.orderId || 'N/A',
    // Use backend-provided lab name, fall back to display name mapping
    labName:
      orderDetail?.patientServiceCenter?.metadata.labName ||
      getLabDisplayName(orderDetail?.labProvider) ||
      'Quest Diagnostics',
    address: formatPatientAddress(
      orderDetail?.patientServiceCenter
        ? {
            line1: orderDetail.patientServiceCenter.metadata.address,
            city: orderDetail.patientServiceCenter.metadata.city,
            state: orderDetail.patientServiceCenter.metadata.state,
            postalCode: orderDetail.patientServiceCenter.metadata.zip,
          }
        : undefined,
    ),
    // Note: Distance is omitted here - will be added when backend provides PSC distance data
  };

  const handleOrderClick = () => {
    navigate(ROUTE_LABS_ORDER_STEP_1);
  };

  const handleContactSupport = () => {
    IntercomShow();
  };

  const handleTryAgain = () => {
    navigate(ROUTE_LABS_ORDER_STEP_1);
  };

  const handleDownloadOrder = () => {
    if (orderDetail?.orderId) {
      downloadOrder(orderDetail.orderId);
    }
  };

  const handleDownloadResults = () => {
    if (latestOrder?.orderId) {
      downloadResults(latestOrder.orderId);
    }
  };

  const handleScheduleVisit = () => {
    callModalViewed('Schedule Labs')
    setIsScheduleModalOpen(true);
  };

  const handleScheduleLabVisit = () => {
    // Use backend-provided scheduling URL with fallback to Quest
    const schedulingUrl =
      orderDetail?.schedulingUrl ||
      'https://appointment.questdiagnostics.com/schedule-appointment/as-personal-information';
    window.open(schedulingUrl, '_blank');
    callLinkClicked('Schedule', schedulingUrl)
    setIsScheduleModalOpen(false);
  };

  const handleGetDirections = () => {
    const address = orderInfo.address;
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank');
  };

  const handleScrollToCategory = (categorySlug: string) => {
    const targetElement = categoryRefs.current[categorySlug];
    if (targetElement) {
      const elementPosition = targetElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - 80; // 80px offset for better visibility

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      <DashboardNavigationBar activeItem="labs" />

      {/* Critical Lab Notifications */}
      {notifications.length > 0 && (
        <CriticalLabNotificationBanner
          notification={notifications[0]}
          onViewResults={() => handleViewResults(notifications[0].notificationId)}
          onDismiss={() => handleDismissNotification(notifications[0].notificationId)}
          isLoading={isDismissing}
        />
      )}

      {/* Render appropriate header based on status */}
      {topLevelStatus === TopLevelOrderStatus.eligible && <LabsHeaderNoOrder onOrderClick={handleOrderClick} />}
      {topLevelStatus === TopLevelOrderStatus.ready_for_testing && (
        <LabsHeaderOrdered
          orderInfo={orderInfo}
          onDownloadOrder={handleDownloadOrder}
          onScheduleVisit={handleScheduleVisit}
          onGetDirections={handleGetDirections}
        />
      )}
      {/* Note: pending_review state is not shown in UI because Junction doesn't send webhooks
          when labs are drawn, only when results are available. Backend may return pending_review
          for COMPLETED status (results received, being processed), but we skip showing a special
          header for this brief intermediate state. */}
      {topLevelStatus === TopLevelOrderStatus.results_ready && (
        <LabsHeaderBiomarkers latestOrder={latestOrder} onCategoryClick={handleScrollToCategory} />
      )}
      {!topLevelStatus && <LabsHeaderError onTryAgain={handleTryAgain} onContactSupport={handleContactSupport} />}

      {/* Schedule Visit Modal */}
      <ScheduleVisitModal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        onSchedule={handleScheduleLabVisit}
        labName={orderInfo.labName}
      />

      <DashboardLayout>
        <DashboardGrid>
          <BiomarkerDashboard categoryRefs={categoryRefs} />
          <div className="flex min-w-full flex-col gap-6">
            {/* Show progress tracker unless results complete */}
            {topLevelStatus && topLevelStatus !== TopLevelOrderStatus.results_ready && (
              <LabsProgressTracker status={topLevelStatus} />
            )}
            {/* Show download report when results are ready */}
            {topLevelStatus === TopLevelOrderStatus.results_ready && latestOrder?.orderId && (
              <LabsDownloadReport onDownload={handleDownloadResults} />
            )}
            <Resources as="aside" width="full" />
          </div>
        </DashboardGrid>
      </DashboardLayout>
    </>
  );
}
